import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, Edit2, Trash2, GripVertical } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import backIcon from '../../../Images/addstarsbackIcon.png';
import '../../Dashboard/Darts/masterConfiguration/productConfiguration/statuses.css';
import '../../Dashboard/Darts/masterConfiguration/productConfiguration/stages.css';
import Editicon from "../../../Images/editstages.png";
import { ChromePicker } from 'react-color';
import { useQuery, useMutation } from '@apollo/client';
import { GET_ALL_STAGES_FOR_JOBROLE, ADD_UPDATE_STAGE, DELETE_STAGE, REORDER_STAGES } from '../../../services/userAPI';
import { message } from 'antd';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import PageBreadcrumb from '../../../reusableField/PageBreadcrumb';

const AdminStages = () => {
    const [stages, setStages] = useState([]);
    const [originalStages, setOriginalStages] = useState([]);
    const [showColorPicker, setShowColorPicker] = useState(false);
    const [selectedStageId, setSelectedStageId] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [newStageName, setNewStageName] = useState('');
    const [newStageColor, setNewStageColor] = useState('#000000');
    const [nameError, setNameError] = useState('');
    const [colorError, setColorError] = useState('');
    const [editedStageId, setEditedStageId] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [stageToDelete, setStageToDelete] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isReordering, setIsReordering] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editingName, setEditingName] = useState('');
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);
    const inputRef = useRef(null);

    const location = useLocation();
    const navigate = useNavigate();
    const jobroleId = location.state?.roleId || 1;

    const { data, loading, error, refetch } = useQuery(GET_ALL_STAGES_FOR_JOBROLE, {
        variables: { jobroleId },
        onCompleted: (data) => {
            if (data.getAllStagesForJobrole) {
                const formattedStages = data.getAllStagesForJobrole.map(stage => ({
                    id: stage.id,
                    name: stage.stageName,
                    color: stage.stageColor ? `#${stage.stageColor.substring(2)}` : '',
                }));
                setStages(formattedStages);
                setOriginalStages([...formattedStages]);
            }
        },
    });

    const [addUpdateStage] = useMutation(ADD_UPDATE_STAGE);
    const [deleteStage] = useMutation(DELETE_STAGE);
    const [reorderStages] = useMutation(REORDER_STAGES);

    const dartsFormNeeded = location.state?.dartsFormNeeded;
    const accountId = location.state?.accountId;
    const roleId = location.state?.roleId;
    const pocName = location.state?.pocName;
    const pocEmail = location.state?.pocEmail;
    const pocNumber = location.state?.pocNumber;
    const numberLogin = location.state?.numberLogin;
    const emailLogin = location.state?.emailLogin;
    const mandatoryTimeDistribution = location.state?.mandatoryTimeDistribution;
    const targetEditDays = location.state?.targetEditDays;
    const inputEditDays = location.state?.inputEditDays;
    const name = location.state?.name;
    const dartsNeeded = location.state?.dartsNeeded;
    const clanNeeded = location.state?.clanNeeded;
    const syncNeeded = location.state?.syncNeeded;
    const accountName = location.state?.accountName;

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, [editingId, showAddModal]);

    const handleAddStage = () => {
        setShowAddModal(true);
        setEditedStageId(null);
        setNewStageName('');
        setNewStageColor('#000000');
        setNameError('');
        setColorError('');
    };

    const handleSaveStage = async () => {
        let hasError = false;

        if (!newStageName.trim()) {
            setNameError('Stage name is required.');
            hasError = true;
        } else {
            setNameError('');
        }

        if (!newStageColor || newStageColor === '#000000') {
            setColorError('Please select a valid color.');
            hasError = true;
        } else {
            setColorError('');
        }

        if (!hasError) {
            setIsSaving(true);
            const colorCode = newStageColor.replace('#', '');
            try {
                const { data } = await addUpdateStage({
                    variables: {
                        jobroleId,
                        stageCategoryId: 1,
                        stageName: newStageName.trim(),
                        stageId: editedStageId,
                        colorCode
                    }
                });
                if (data.addUpdateStage.success) {
                    if (editedStageId) {
                        const updatedStages = stages.map(stage =>
                            stage.id === editedStageId ? { ...stage, name: newStageName.trim(), color: newStageColor } : stage
                        );
                        setStages(updatedStages);
                        setOriginalStages([...updatedStages]);
                        message.success('Stage updated successfully!');
                    } else {
                        const newStage = {
                            id: data.addUpdateStage.data.id,
                            name: newStageName.trim(),
                            color: newStageColor
                        };
                        const updatedStages = [...stages, newStage];
                        setStages(updatedStages);
                        setOriginalStages([...updatedStages]);
                        message.success('Stage added successfully!');
                    }
                    setNewStageName('');
                    setNewStageColor('#000000');
                    setShowAddModal(false);
                    refetch();
                } else {
                    setNameError(data.addUpdateStage.message || 'Failed to save stage.');
                    message.error(data.addUpdateStage.message || 'Failed to save stage.');
                }
            } catch (error) {
                setNameError('An error occurred while saving the stage.');
                message.error('An error occurred while saving the stage.');
                console.error(error);
            } finally {
                setIsSaving(false);
            }
        }
    };

    const handleEditStage = (id) => {
        const stage = stages.find(s => s.id === id);
        if (stage) {
            setShowAddModal(true);
            setEditedStageId(id);
            setNewStageName(stage.name);
            setNewStageColor(stage.color);
            setNameError('');
            setColorError('');
        }
    };

    const handleSaveEdit = async () => {
        if (editingName.trim()) {
            try {
                const stage = stages.find(s => s.id === editingId);
                const colorCode = stage.color.replace('#', '');
                const { data } = await addUpdateStage({
                    variables: {
                        jobroleId,
                        stageCategoryId: 1,
                        stageName: editingName.trim(),
                        stageId: editingId,
                        colorCode
                    }
                });
                if (data.addUpdateStage.success) {
                    setStages(stages.map(stage =>
                        stage.id === editingId ? { ...stage, name: editingName.trim() } : stage
                    ));
                    setEditingId(null);
                    setEditingName('');
                    message.success('Stage updated successfully!');
                } else {
                    setNameError(data.addUpdateStage.message || 'Failed to update stage.');
                    message.error(data.addUpdateStage.message || 'Failed to update stage.');
                }
            } catch (error) {
                setNameError('An error occurred while updating the stage.');
                message.error('An error occurred while updating the stage.');
            }
        }
    };

    const handleDeleteStage = (id) => {
        const stage = stages.find(s => s.id === id);
        if (stage) {
            setStageToDelete(stage);
            setShowDeleteModal(true);
        }
    };

    const confirmDelete = async () => {
        if (stageToDelete) {
            setIsDeleting(true);
            try {
                const { data } = await deleteStage({
                    variables: { stageId: stageToDelete.id }
                });
                if (data.deleteStage.success) {
                    setStages(stages.filter(stage => stage.id !== stageToDelete.id));
                    message.success('Stage deleted successfully!');
                } else {
                    setNameError(data.deleteStage.message || 'Failed to delete stage.');
                    message.error(data.deleteStage.message || 'Failed to delete stage.');
                }
            } catch (error) {
                setNameError('An error occurred while deleting the stage.');
                message.error('An error occurred while deleting the stage.');
            } finally {
                setIsDeleting(false);
                setShowDeleteModal(false);
                setStageToDelete(null);
            }
        }
    };

    const cancelDelete = () => {
        setShowDeleteModal(false);
        setStageToDelete(null);
    };

    const handleKeyDown = (e, action) => {
        if (e.key === 'Enter') {
            action();
        } else if (e.key === 'Escape') {
            if (editingId) {
                handleCancelEdit();
            } else if (showAddModal) {
                setShowAddModal(false);
                setNameError('');
                setColorError('');
            }
        }
    };

    const handleDragStart = (e, index) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (draggedIndex !== null && draggedIndex !== index) {
            setDragOverIndex(index);
        }
    };

    const handleDragLeave = () => {
        setDragOverIndex(null);
    };

    const handleDrop = (e, dropIndex) => {
        e.preventDefault();

        if (draggedIndex === null || draggedIndex === dropIndex) {
            setDraggedIndex(null);
            setDragOverIndex(null);
            return;
        }

        const newStages = [...stages];
        const draggedItem = newStages[draggedIndex];
        newStages.splice(draggedIndex, 1);
        newStages.splice(dropIndex, 0, draggedItem);

        setStages(newStages);
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditingName('');
    };

    const handleBack = () => {
        navigate("/admin-home");
    };

    const handleBackToViewRole = () => {
        const safeRoleId = roleId || location.state?.roleId;
        const safeAccountName = accountName || location.state?.accountName;

        navigate("/admin-view-role", {
            state: {
                roleId: safeRoleId,
                accountId: accountId || location.state?.accountId,
                accountName: safeAccountName,
                name: name || location.state?.name,
                dartsNeeded: dartsNeeded || location.state?.dartsNeeded,
                clanNeeded: clanNeeded || location.state?.clanNeeded,
                syncNeeded: syncNeeded || location.state?.syncNeeded,
                dartsFormNeeded: dartsFormNeeded || location.state?.dartsFormNeeded,
                pocName: pocName || location.state?.pocName,
                pocEmail: pocEmail || location.state?.pocEmail,
                pocNumber: pocNumber || location.state?.pocNumber,
                numberLogin: numberLogin || location.state?.numberLogin,
                emailLogin: emailLogin || location.state?.emailLogin,
                mandatoryTimeDistribution: mandatoryTimeDistribution || location.state?.mandatoryTimeDistribution,
                targetEditDays: targetEditDays || location.state?.targetEditDays,
                inputEditDays: inputEditDays || location.state?.inputEditDays,
            },
        });
    };

    const handleDiscardClick = () => {
        navigate("/admin-home");
    };

    const handleReorder = async () => {
        setIsReordering(true);
        try {
            const reorderIds = stages.map(stage => stage.id);
            const { data } = await reorderStages({
                variables: { jobroleId, reorderIds }
            });
            if (data.reorderStages.success) {
                setOriginalStages([...stages]);
                message.success('Stages reordered successfully!');
            } else {
                setNameError(data.reorderStages.message || 'Failed to reorder stages.');
                setStages([...originalStages]);
                message.error(data.reorderStages.message || 'Failed to reorder stages.');
            }
        } catch (error) {
            setNameError('An error occurred while reordering stages.');
            setStages([...originalStages]);
            message.error('An error occurred while reordering stages.');
            console.error(error);
        } finally {
            setIsReordering(false);
        }
    };

    const handleReset = () => {
        setStages([...originalStages]);
        message.success('Stages reset to original order!');
    };

    const handleColorClick = (id) => {
        setSelectedStageId(id);
        setShowColorPicker(true);
    };

    const handleColorChange = (color) => {
        if (selectedStageId !== null) {
            setStages(stages.map(stage =>
                stage.id === selectedStageId ? { ...stage, color: color.hex } : stage
            ));
        } else if (showAddModal) {
            setNewStageColor(color.hex);
            setColorError('');
        }
    };

    if (loading) return <div>Loading...</div>;
    if (error) return <div>Error: {error.message}</div>;

    const filteredStages = stages.filter(stage =>
        stage.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="user-management-container">
            <PageBreadcrumb homePath="/admin">
                <span onClick={handleBack} style={{ cursor: 'pointer' }}>{accountName}</span>
                <span>&gt;</span>
                <span onClick={() => handleBackToViewRole({ state: { name, accountName, roleId, dartsFormNeeded, dartsNeeded, syncNeeded } })} style={{ cursor: 'pointer' }}>{name}</span>
                <span>&gt;</span>
                <span style={{ fontWeight: 'bold' }}>Darts</span>
            </PageBreadcrumb>

            <div>
                <div className="tabs-container-stages">
                    <span className="config-label" onClick={() => navigate('/admin-product-configuration', { state: { name, accountName, roleId, dartsFormNeeded, dartsNeeded, roleId, accountId, clanNeeded, syncNeeded } })}>
                        <span>LDMS Configuration</span>
                    </span>
                    <span className="tabs-stages">
                        <button
                            className="tab-btn"
                            onClick={() => navigate('/admin-activities', { state: { name, accountName, roleId, dartsFormNeeded, dartsNeeded, roleId, accountId, clanNeeded, syncNeeded } })}
                        >
                            Activities
                        </button>
                        <button
                            className="tab-btn"
                            onClick={() => navigate('/admin-statuses', { state: { name, accountName, roleId, dartsFormNeeded, dartsNeeded, roleId, accountId, clanNeeded, syncNeeded } })}
                        >
                            Statuses
                        </button>
                        <button className="tab-btn active">Stages</button>
                        <button
                            className="tab-btn"
                            onClick={() => navigate('/forms-configuration', { state: { name, accountName, roleId, dartsFormNeeded, dartsNeeded, roleId, accountId, clanNeeded, syncNeeded } })}
                        >
                            Forms Configuration
                        </button>
                    </span>
                </div>

                <div style={{
                    borderRadius: '4px',
                    marginBottom: '16px',
                    display: 'flex',
                    gap: '12px',
                    alignItems: 'center'
                }}>
                    <input
                        type="text"
                        placeholder="Search stage name"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{
                            flex: 1,
                            padding: '10px 12px',
                            border: '1px solid #a3a3a3',
                            borderRadius: '4px',
                            fontSize: '14px',
                            outline: 'none'
                        }}
                    />
                    <button
                        onClick={handleAddStage}
                        style={{
                            padding: '10px 24px',
                            backgroundColor: '#AD0101',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: 500
                        }}
                    >
                        Add Stages
                    </button>
                </div>

                {filteredStages.length === 0 ? (
                    <div style={{
                        textAlign: 'center',
                        padding: '16px',
                        fontFamily: 'Inter',
                        color: '#666'
                    }}>
                        No stages found.
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {filteredStages.map((stage, index) => (
                            <div
                                key={stage.id}
                                draggable={editingId !== stage.id}
                                onDragStart={(e) => handleDragStart(e, index)}
                                onDragOver={(e) => handleDragOver(e, index)}
                                onDragLeave={handleDragLeave}
                                onDrop={(e) => handleDrop(e, index)}
                                onDragEnd={handleDragEnd}
                                style={{
                                    backgroundColor: 'white',
                                    padding: '16px',
                                    borderRadius: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    border: dragOverIndex === index ? '2px solid #1890ff' : '1px solid #e8e8e8',
                                    opacity: draggedIndex === index ? 0.5 : 1,
                                    cursor: editingId === stage.id ? 'default' : 'move',
                                    transition: 'all 0.2s ease',
                                    transform: dragOverIndex === index ? 'scale(1.02)' : 'scale(1)',
                                    boxShadow: dragOverIndex === index ? '0 4px 12px rgba(0,0,0,0.1)' : 'none'
                                }}
                            >
                                <DragIndicatorIcon
                                    size={20}
                                    color="#0b0b0bff"
                                    style={{
                                        cursor: editingId === stage.id ? 'default' : 'grab',
                                        flexShrink: 0
                                    }}
                                />

                                {editingId === stage.id ? (
                                    <>
                                        <input
                                            ref={inputRef}
                                            type="text"
                                            value={editingName}
                                            onChange={(e) => setEditingName(e.target.value)}
                                            onKeyDown={(e) => handleKeyDown(e, handleSaveEdit)}
                                            style={{
                                                flex: 1,
                                                padding: '8px 12px',
                                                border: '1px solid #d9d9d9',
                                                borderRadius: '4px',
                                                fontSize: '14px',
                                                outline: 'none'
                                            }}
                                        />
                                        <button
                                            onClick={handleSaveEdit}
                                            style={{
                                                padding: '6px 16px',
                                                backgroundColor: '#AD0101',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                fontSize: '14px'
                                            }}
                                        >
                                            {isSaving ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : 'Save'}
                                        </button>
                                        <button
                                            onClick={handleCancelEdit}
                                            style={{
                                                padding: '6px 16px',
                                                backgroundColor: '#d9d9d9',
                                                color: '#333',
                                                border: 'none',
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                fontSize: '14px'
                                            }}
                                        >
                                            Cancel
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '12px',
                                            flex: 1,
                                        }}>
                                            <div
                                                style={{
                                                    width: '16px',
                                                    height: '16px',
                                                    borderRadius: '50%',
                                                    backgroundColor: stage.color,
                                                }}
                                                onClick={() => handleColorClick(stage.id)}
                                            />
                                            <span style={{
                                                fontSize: '14px',
                                                fontWeight: '500',
                                                fontFamily: 'Inter',
                                            }}>
                                                {stage.name}
                                            </span>
                                        </div>
                                        <button
                                            onClick={() => handleEditStage(stage.id)}
                                            style={{
                                                padding: '6px',
                                                backgroundColor: 'transparent',
                                                border: 'none',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center'
                                            }}
                                        >
                                            <img src={Editicon} alt="Edit" style={{ width: '18px', height: '18px' }} />
                                        </button>
                                        <button
                                            onClick={() => handleDeleteStage(stage.id)}
                                            style={{
                                                padding: '6px',
                                                backgroundColor: 'transparent',
                                                border: 'none',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center'
                                            }}
                                        >
                                            <Trash2 size={18} color="#AD0101" />
                                        </button>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {showAddModal && (
                    <div style={{
                        position: 'fixed',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 1001, // Above header
                        backgroundColor: 'white',
                        padding: '20px',
                        borderRadius: '4px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        width: '300px',
                        textAlign: 'center'
                    }}>
                        <h3 style={{
                            fontFamily: 'Inter',
                            fontSize: '18px',
                            fontWeight: '600',
                            marginBottom: '15px',
                            color: '#333'
                        }}>{editedStageId ? 'Edit Stage' : 'Add New Stage'}</h3>
                        <div style={{ textAlign: 'left', marginBottom: '15px' }}>
                            <label style={{
                                fontFamily: 'Inter',
                                fontSize: '14px',
                                fontWeight: '500',
                                color: '#333'
                            }}>
                                Stage Name <span style={{ color: '#AD0101' }}>*</span>
                            </label>
                            <input
                                ref={inputRef}
                                type="text"
                                placeholder="Enter stage name"
                                value={newStageName}
                                onChange={(e) => {
                                    setNewStageName(e.target.value);
                                    const searchInput = document.querySelector('input[type="text"]:first-of-type');
                                    if (searchInput) searchInput.value = e.target.value;
                                }}
                                onKeyDown={(e) => handleKeyDown(e, handleSaveStage)}
                                style={{
                                    width: '100%',
                                    padding: '8px 12px',
                                    marginTop: '5px',
                                    border: '1px solid #a3a3a3',
                                    borderRadius: '4px',
                                    fontSize: '14px',
                                    outline: 'none'
                                }}
                            />
                            {nameError && (
                                <p style={{
                                    color: '#AD0101',
                                    fontSize: '12px',
                                    marginTop: '5px',
                                    textAlign: 'left'
                                }}>{nameError}</p>
                            )}
                        </div>
                        <div style={{ textAlign: 'left', marginBottom: '20px' }}>
                            <label style={{
                                fontFamily: 'Inter',
                                fontSize: '14px',
                                fontWeight: '500',
                                color: '#333'
                            }}>
                                Color <span style={{ color: '#AD0101' }}>*</span>
                            </label>
                            <ChromePicker
                                color={newStageColor}
                                onChange={handleColorChange}
                                disableAlpha={true}
                                width="200px"
                                style={{ marginTop: '5px' }}
                            />
                            {colorError && (
                                <p style={{
                                    color: '#AD0101',
                                    fontSize: '12px',
                                    marginTop: '5px',
                                    textAlign: 'left'
                                }}>{colorError}</p>
                            )}
                        </div>
                        <div>
                            <button
                                onClick={handleSaveStage}
                                disabled={isSaving}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: isSaving ? '#a3a3a3' : '#AD0101',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: isSaving ? 'not-allowed' : 'pointer',
                                    fontSize: '14px',
                                    marginRight: '10px'
                                }}
                            >
                                {isSaving ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : (editedStageId ? 'Update' : 'Save')}
                            </button>
                            <button
                                onClick={() => {
                                    setShowAddModal(false);
                                    setNameError('');
                                    setColorError('');
                                }}
                                disabled={isSaving}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#d9d9d9',
                                    color: '#333',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: isSaving ? 'not-allowed' : 'pointer',
                                    fontSize: '14px'
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}

                {showDeleteModal && stageToDelete && (
                    <div style={{
                        position: 'fixed',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 1001, // Above header
                        backgroundColor: 'white',
                        padding: '20px',
                        borderRadius: '4px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        width: '300px',
                        textAlign: 'center'
                    }}>
                        <h3 style={{
                            fontFamily: 'Inter',
                            fontSize: '18px',
                            fontWeight: '600',
                            marginBottom: '15px',
                            color: '#333'
                        }}>Confirm Delete</h3>
                        <p style={{
                            fontFamily: 'Inter',
                            fontSize: '14px',
                            color: '#333',
                            marginBottom: '20px'
                        }}>
                            Do you want to delete this '{stageToDelete.name}'?
                        </p>
                        <div>
                            <button
                                onClick={confirmDelete}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#AD0101',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    marginRight: '10px'
                                }}
                            >
                                {isDeleting ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : 'Yes'}
                            </button>
                            <button
                                onClick={cancelDelete}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#d9d9d9',
                                    color: '#333',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '14px'
                                }}
                            >
                                No
                            </button>
                        </div>
                    </div>
                )}

                {(showAddModal || showDeleteModal) && (
                    <div
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            zIndex: 1000 // Above sidebar, below modals
                        }}
                        onClick={() => {
                            if (showAddModal) {
                                setShowAddModal(false);
                                setNameError('');
                                setColorError('');
                            }
                            if (showDeleteModal) {
                                setShowDeleteModal(false);
                                setStageToDelete(null);
                            }
                        }}
                    />
                )}

                {filteredStages.length > 0 && (
                    <div style={{
                        marginTop: '24px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <a href="#" style={{
                            color: '#1890ff',
                            textDecoration: 'none',
                            fontSize: '14px'
                        }}>
                        </a>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button
                                onClick={handleReset}
                                style={{
                                    padding: '8px 24px',
                                    backgroundColor: 'white',
                                    color: '#333',
                                    border: '1px solid #d9d9d9',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '14px'
                                }}
                            >
                                Reset
                            </button>
                            <button
                                onClick={handleReorder}
                                style={{
                                    padding: '8px 24px',
                                    backgroundColor: '#AD0101',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500'
                                }}
                            >
                                {isReordering ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : 'Reorder'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminStages;